import { EndUserComponent } from '@aurora/shared-types/pages/enums';
import React, { useId } from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import useTranslation from '../../useTranslation';
import localStyles from './SwitchBranchFloatingBox.module.pcss';
import type { FloatingControlDropdownItems } from '../../common/FloatingControlBox/FloatingControlBox';
import FloatingControlBox from '../../common/FloatingControlBox/FloatingControlBox';

export const SWITCH_BRANCH_FLOATING_BOX_ID = 'switchBranch';

interface Props {
  /**
   * Current active branch name.
   */
  activeBranch: string | null;

  /**
   * Callback to be used to switch back to the main branch.
   */
  onSwitchToMainBranch: () => void;

  /**
   * Callback to be used to switch to another branch.
   */
  onSwitchToBranch: () => void;
}

/**
 * Switch branch floating button. It is displayed when Aurora is not using the main branch.
 *
 * @author Dolan Halbrook, Willi Hyde
 */
const SwitchBranchFloatingBox: React.FC<React.PropsWithChildren<Props>> = ({
  activeBranch,
  onSwitchToMainBranch,
  onSwitchToBranch
}) => {
  const cx = useClassNameMapper(localStyles);
  const {
    formatMessage,
    loading: textLoading,
    FormattedMessage
  } = useTranslation(EndUserComponent.SWITCH_BRANCH_FLOATING_BOX);

  const id = useId();
  const ariaId = `switch-user-menu-${id}`;

  if (textLoading) {
    return null;
  }

  const switchBranchMenuItems: Array<FloatingControlDropdownItems> = [
    {
      onClick: () => onSwitchToBranch(),
      title: formatMessage('switchToAnotherBranch'),
      id: 'another'
    },
    {
      onClick: () => onSwitchToMainBranch(),
      title: (
        <FormattedMessage
          id="switchBackToMain"
          values={{
            strong: function renderChunks(chunks) {
              return <strong>{chunks}</strong>;
            }
          }}
        />
      ),
      id: 'main'
    }
  ];

  return (
    <FloatingControlBox
      testId="SwitchBranchFloatingBox"
      dropdownId={ariaId}
      dropdownToggleLabel={formatMessage('toggleButtonLabel')}
      dropdownMenuItems={switchBranchMenuItems}
    >
      <div className={cx('lia-description')}>
        <FormattedMessage
          id="description"
          values={{
            activeBranch,
            strong: function renderChunks(chunks) {
              return <strong>{chunks}</strong>;
            }
          }}
        />
      </div>
    </FloatingControlBox>
  );
};

export default SwitchBranchFloatingBox;
